import { Client } from "@twilio/conversations";
import { Dispatch } from "redux";
import log from "loglevel";

import { sessionDataHandler } from "../../../sessionDataHandler";
import { notifications } from "../../../notifications";
import { addNotification, removeNotification } from "../genericActions";
import { ACTION_UPDATE_SESSION_DATA } from "../actionTypes";
import { reportClientError } from "../../../utils/reportClientError";
import { extrairCodigos } from "../../../utils/diagnosticoDeEnvio";
import { descreverErro } from "../../../utils/descreverErro";
import { deveReconectar, reconectar } from "./reconnect";
import { revalidarEstadoDaConversa } from "./revalidarConversa";

/** Handler de `visibilitychange` do módulo — removido antes de re-registrar (ver abaixo). */
let handlerDeVisibilidade: (() => void) | null = null;

export const initClientListeners = (conversationClient: Client, dispatch: Dispatch, conversationSid: string) => {
    const tokenAboutToExpireEvent = "tokenAboutToExpire";
    const connectionStateChangedEvent = "connectionStateChanged";

    // remove any other refresh handler added before and add it again
    conversationClient.removeAllListeners(tokenAboutToExpireEvent);
    conversationClient.addListener(tokenAboutToExpireEvent, async () => {
        log.debug("conversationClientListener: token about to expire");

        /*
         * 🔴 A RENOVAÇÃO FALHAVA EM SILÊNCIO. Sem try/catch, uma rejeição aqui virava unhandled
         * rejection; e um retorno vazio simplesmente não entrava no `if` e o listener terminava
         * satisfeito. Nos dois casos o token EXPIRA, o apostador perde o chat no meio da conversa,
         * e o único registro do acontecido era ele reclamando depois.
         *
         * O comportamento não muda: continua sem travar nada e sem mexer na tela. O que muda é
         * a operação passar a saber. Tela para este caso é decisão de produto, em PR próprio.
         */
        try {
            const data = await sessionDataHandler.getUpdatedToken();
            if (data?.token && data?.conversationSid) {
                await conversationClient.updateToken(data.token);
                dispatch({
                    type: ACTION_UPDATE_SESSION_DATA,
                    payload: {
                        token: data.token,
                        conversationSid: data.conversationSid
                    }
                });
                return;
            }
            reportClientError("token_refresh_failed", { reason: "renovação voltou sem token utilizável" });
        } catch (e) {
            reportClientError("token_refresh_failed", { reason: descreverErro(e), ...extrairCodigos(e) });
        }
    });

    conversationClient.removeAllListeners(connectionStateChangedEvent);
    conversationClient.addListener(connectionStateChangedEvent, (connectionStatus: string) => {
        if (connectionStatus === "connected") {
            dispatch(removeNotification(notifications.noConnectionNotification().id));
            return;
        }
        if (connectionStatus === "connecting") {
            dispatch(addNotification(notifications.noConnectionNotification()));
            return;
        }
        /*
         * `denied` é o token recusado pelo Twilio. A partir do #15 o widget TENTA reconectar sozinho
         * (o `reconectar` renova o token), então isto deixou de ser terminal — mas continua sendo a
         * CAUSA que a operação precisa ver. São dois fatos diferentes e ambos úteis:
         *   `connection_denied` = o token foi recusado;
         *   `reconnect_failed`  = a recuperação automática desistiu (emitido pelo `reconectar`).
         */
        if (connectionStatus === "denied") {
            reportClientError("connection_denied", {
                connectionState: "denied",
                reason: "token recusado pelo Twilio — tentando reconectar"
            });
        }
        if (deveReconectar(connectionStatus)) {
            /*
             * Antes isto caía no VÁCUO: o SDK desistia (`disconnected`/`denied`/`error`) e o chat
             * ficava mudo, sem aviso, até o apostador dar F5 — que ele não tem motivo pra saber.
             * `retrying` fica de fora de propósito (o SDK está se recuperando sozinho).
             */
            void reconectar({ client: conversationClient, dispatch, conversationSid, origem: "conexao_perdida" });
        }
    });

    /*
     * Aba em segundo plano: o navegador congela timers, o socket morre e NENHUM evento de
     * `connectionStateChanged` chega — então ao voltar a checagem tem que ser nossa. Só age se a
     * conexão estiver doente; reconectar um client saudável é trabalho e risco à toa.
     *
     * O handler é guardado no módulo pra ser removido antes de registrar de novo — o Tier 2
     * re-registra os listeners num client novo, e sem isso acumularia um handler por reconexão.
     */
    if (handlerDeVisibilidade) {
        document.removeEventListener("visibilitychange", handlerDeVisibilidade);
    }
    handlerDeVisibilidade = () => {
        if (document.visibilityState !== "visible") {
            return;
        }
        /*
         * Revalida SEMPRE que a aba volta, mesmo com a conexão parecendo saudável — e é aqui que
         * mora o caso de produção. Reproduzido ao vivo (2026-08-28): com a aba fora, a conversa foi
         * encerrada, o evento se perdeu, e o widget seguiu com o composer montado achando `active`;
         * o cliente digitou e levou 400. Reconectar sozinho não resolve, porque o SDK não
         * re-sincroniza o estado, e o `connectionState` pode nem acusar nada.
         *
         * Custo: uma leitura por retorno de aba, best-effort e fora do caminho do usuário.
         */
        void revalidarEstadoDaConversa({ client: conversationClient, conversationSid, dispatch });

        if (deveReconectar(conversationClient.connectionState)) {
            /*
             * `reiniciarTentativas`: a volta da aba é uma oportunidade NOVA, dirigida pelo cliente —
             * não a nossa insistência. Sem isto, se o orçamento já tivesse esgotado numa queda
             * anterior, esta checagem nasceria morta (bug pego no navegador).
             */
            void reconectar({ client: conversationClient, dispatch, conversationSid, reiniciarTentativas: true, origem: "volta_de_aba" });
        }
    };
    document.addEventListener("visibilitychange", handlerDeVisibilidade);
};
